import { checkGroundingService, getGroundingConfigurationByVIN } from 'apis/groundingApis'
import { CustomLoader } from 'components/Loader'
import { Formik } from 'formik'
import { useEffect, useState } from 'react'
import { Alert, Button, Col, Form, Row } from 'react-bootstrap'
import * as Yup from 'yup'
import shallow from 'zustand/shallow'
import { GroundingRenderStep } from './store/RenderStep'
import { useGroundingStore } from './store/useGroundingStore'
import { IVinSearchForm, initialVinValues } from 'types/Grounding/groundingTypes'
import { useGlobalStore } from 'store/useGlobalStore'
import { SessionStorageKey, VIN_CHARACTERS_REGEX } from 'common/constants'
import { useHistory } from 'react-router-dom'
import { StyledGroundingWithVIN } from './GroundingWithVIN.styled'
import { useGroundingActionStore } from './store/useGroundingActionStore'

const schema = Yup.object({
  VIN: Yup.string().length(17, 'Must be 17 characters').required('Required')
})

export interface IProps {
  setLocationSelected?: (locationID: number) => void
  handleSetDefaultGroundingData?: (data) => void
}

export const GroundingWithVIN = () => {
  const { getFeatureToggle } = useGlobalStore()
  const showGroundingPageLoadingBar = getFeatureToggle('GroundingPageLoadingBar') as string
  const [setGroundingData, setDefaultGroundingData, setAlreadyConfirmRecall, setAlreadyConfirmGeneralModal] =
    useGroundingStore(
      (state) => [
        state.setGroundingData,
        state.setDefaultGroundingData,
        state.setAlreadyConfirmRecall,
        state.setAlreadyConfirmGeneralModal
      ],
      shallow
    )

  const [setActiveStep] = useGroundingActionStore((state) => [state.setActiveStep], shallow)

  const [showMessage, setShowMessage] = useState(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [hasAlreadyChanged, setHasAlreadyChanged] = useState<boolean>(false)
  const getLocalText = useGlobalStore((state) => state.getLocalText)

  const history = useHistory()

  const handleSearchGrounding = (values: IVinSearchForm) => {
    if (values?.VIN) {
      getInitialGroundingData(values?.VIN)
    }
  }

  useEffect(() => {
    setAlreadyConfirmRecall(false)
    setAlreadyConfirmGeneralModal(false)
    setGroundingData(null)
    let isServiceRunning = checkGroundingService()
    if (!isServiceRunning) {
      setErrorMessage(
        'We are currently experiencing technical difficulties with the grounding process.  Please try back again in a few minutes. We apologize for the inconvenience.'
      )
      setShowMessage(true)
      return
    }
    let VIN = sessionStorage.getItem(SessionStorageKey.GROUND_VIN)
    if (VIN !== null && VIN !== '') {
      sessionStorage.setItem(SessionStorageKey.GROUND_VIN, '')
      initialVinValues.VIN = VIN
      setHasAlreadyChanged(true)
      getInitialGroundingData(VIN)
    }
  }, [])

  const getInitialGroundingData = async (VIN: string) => {
    try {
      setIsLoading(true)
      const response = await getGroundingConfigurationByVIN(VIN)

      if (response) {
        if ((response.ErrorMessage !== undefined && response.ErrorMessage !== '') || response.SavePayoffError) {
          setErrorMessage(response.ErrorMessage ?? response.SavePayoffError)
          setShowMessage(true)
          return
        }
        response.GroundingNotice = response.GroundingNotice?.replace(
          '<a href="#terms-modal" data-toggle="modal">Terms and Conditions</a>',
          '<a href="" id="termsLink" >Terms and Conditions</a>'
        )
        response.vehicleReturnDate = null
        initialVinValues.VIN = VIN
        setGroundingData(response)
        setDefaultGroundingData(response)
        setActiveStep(GroundingRenderStep.GRD_OPTIONS)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <StyledGroundingWithVIN>
      <Row>
        <Col>
          <Formik
            enableReinitialize
            initialValues={initialVinValues}
            onSubmit={(values) => handleSearchGrounding(values)}
            validationSchema={schema}
            onReset={() => {
              setShowMessage(false)
              initialVinValues.VIN = ''
            }}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit} onReset={props.handleReset}>
                <Row style={{ marginTop: '20px' }}>
                  <Form.Group className="col-lg-4 col-sm-8" controlId="VIN">
                    <Row>
                      <Form.Label column sm="auto">
                        <h5>Enter VIN</h5>
                      </Form.Label>
                      <Col className="p-0">
                        <Form.Control
                          type="input"
                          placeholder="#17 digit VIN"
                          onChange={(e) => {
                            props.handleChange(e)
                            setHasAlreadyChanged(true)
                          }}
                          onBlur={props.handleBlur}
                          value={props.values.VIN}
                          isInvalid={hasAlreadyChanged && !!props.errors.VIN}
                          onKeyDown={(e) => {
                            if (!VIN_CHARACTERS_REGEX.test(e.key)) {
                              e.preventDefault()
                            }
                          }}
                        />
                        <Form.Control.Feedback tooltip={false} type="invalid">
                          {hasAlreadyChanged && props.errors.VIN}
                        </Form.Control.Feedback>
                      </Col>
                    </Row>
                  </Form.Group>
                  <Form.Group className="col-lg-4 col-sm-4 pl-0">
                    <Button type="submit" disabled={!props.isValid} className="mx-2">
                      GROUND
                    </Button>
                    <Button className="btn-secondary" type="reset">
                      CLEAR ALL
                    </Button>
                  </Form.Group>
                </Row>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
      {Number(showGroundingPageLoadingBar) === 1 && isLoading && (
        <Row className="spinner-row">{<CustomLoader size="97px" />}</Row>
      )}
      {!isLoading && (
        <Row>
          <Col lg={{ span: 4 }} sm={{ span: 8 }}>
            {showMessage && (
              <Alert variant="danger" onClose={() => setShowMessage(false)} dismissible>
                {getLocalText(errorMessage, errorMessage)}
              </Alert>
            )}
          </Col>
        </Row>
      )}
    </StyledGroundingWithVIN>
  )
}
